/**
 * Typed client for the Business OS Control Center API.
 *
 * Two deliberate security choices:
 *
 * 1. **The access token is held in memory only.** It is never written to
 *    `localStorage` or `sessionStorage`, so a successful XSS cannot read a stored
 *    token later.
 *
 *    A page reload no longer signs the user out: the session is restored from the
 *    `HttpOnly` refresh cookie via `POST /api/auth/restore` (`api.restore`), which
 *    returns a fresh short-lived access token that lives in memory again. The extra
 *    short-lived token is the price of not persisting credentials on the client.
 *    Concurrent restores with the same idle setting share one request (single-flight)
 *    so a burst of mounts cannot multiply cookie rotations; a failed restore leaves
 *    the current token untouched, and a 401/422 simply means "no session to restore".
 * 2. **The refresh cookie is never touched by JavaScript.** It is `HttpOnly` and
 *    `Secure` (set by the API) and is only sent to `/api/auth/*`, so this client
 *    sends `credentials: "include"` for those calls and nothing else.
 *
 * Every response shape here mirrors `docs/api/openapi.yaml` / the implemented
 * routes. Nothing is invented; where a field is optional in the API it is optional
 * here.
 */

const BASE_URL: string = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export interface Project {
  id: string;
  slug: string;
  name: string;
  kind: "standard" | "pilot";
  program: "megabooker_sales_growth" | "zoho_hotel_solutions" | "minbarlive_growth";
  status: "active" | "paused" | "archived";
  my_role: string;
  kill_switch_engaged: boolean;
}

export interface CurrentUser {
  id: string;
  email: string;
  display_name: string;
  status: string;
  is_org_owner: boolean;
  locale: string;
  last_login_at: string | null;
  memberships: { project_id: string; role: string }[];
  permissions: Record<string, string[]>;
}

export type JobStatus =
  | "queued"
  | "running"
  | "awaiting_approval"
  | "awaiting_budget_approval"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "killed"
  | "needs_review";

export interface Job {
  id: string;
  project_id: string;
  workflow: string;
  status: JobStatus;
  current_step_index: number;
  attempts: number;
  max_attempts: number;
  retry_attempts: number;
  created_by: string | null;
  approval_ids: string[];
  needs_review_reason: string | null;
  error: { code: string; message: string } | null;
  cancel_requested_at: string | null;
  cost_summary: {
    actual_micros: number;
    estimated_micros: number;
    unavailable_count: number;
  } | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export interface JobStep {
  id: string;
  index: number;
  step: string;
  status: string;
  kind: "internal" | "external_read" | "external_write_proposal";
  attempt: number;
  output_hash: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface BusinessRoleView {
  planning_role_id: string;
  name: string;
  name_en: string;
  inputs: string[];
  outputs: string[];
  allowed_capabilities: string[];
  allowed_tools: string[];
  decision_owner: string;
  implementation_status: "implemented_synthetic" | "planned_unavailable";
  runtime_identity: {
    agent_key: string;
    agent_version: string;
    provider_kind: string;
    paid_agent: boolean;
    zia_agent: boolean;
  } | null;
  availability_reason: string | null;
}

export interface BusinessCatalogue {
  workflow: {
    key: string;
    version: number;
    workflow_id: string;
    request_alias: string;
  };
  roles: BusinessRoleView[];
  runtime_agents: {
    agent_key: string;
    agent_version: string;
    planning_role_id: string;
    provider_kind: string;
    paid_agent: boolean;
    zia_agent: boolean;
  }[];
  global_rules: Record<string, boolean>;
}

export interface BusinessProjectContext {
  schema_version: string;
  project_id: string;
  program: string;
  version: number;
  content_hash: string;
  product_summary: string;
  ideal_customer: string;
  allowed_claims: string[];
  disallowed_claims: string[];
  approved_price_context: null;
  sales_stages: string[];
  qualification: string[];
  decision_owner_role: string;
  system_of_record: string;
  access_note: string;
}

export interface BusinessIntegrationState {
  schema: string;
  workflow_key: string;
  project_id: string;
  crm: {
    provider: string;
    live_connection: string;
    verified: boolean;
    synthetic_only: boolean;
    account_ref: null;
    data_centre: null;
    scopes: string[];
    cost: { status: string; amount_micros: number | null };
    object_mapping: {
      object: string;
      business_purpose: string;
      status: string;
      verified_field_ids: null;
    }[];
    probe_checklist: string[];
    controlled_write_checklist: string[];
  };
  zia: {
    availability: string;
    org_ref: null;
    agent_id: null;
    agent_version: null;
    tools: unknown[];
    side_effect_class: string;
    connection_verified: boolean;
    cost_status: string;
    read_only_invocable: boolean;
    reason: string;
  };
  live_calls_made: number;
  credentials_read: boolean;
  private_rows_read: boolean;
}

export interface BusinessCatalogueResponse {
  catalogue: BusinessCatalogue;
  project_context: BusinessProjectContext;
  integration: BusinessIntegrationState;
  workflow: {
    key: string;
    version: number;
    workflow_id: string;
    content_hash: string;
    request_alias: string;
    steps: {
      index: number;
      step_id: string;
      kind: string;
      agent_key: string;
      agent_version: string;
      idempotent: boolean;
    }[];
  };
  synthetic_only: boolean;
  live_connection_verified: boolean;
}

export interface BusinessStepView {
  index: number;
  step_id: string;
  agent_key: string | null;
  agent_version: string | null;
  role: string | null;
  kind: string | null;
  status: string | null;
  output_hash: string | null;
  dependency_reason: string | null;
  source_vs_inference: { source: string[]; inference: string[] } | null;
  summary: Record<string, unknown>;
}

export interface BusinessProposalView {
  id: string;
  status: string;
  action_type: string;
  risk: string;
  content_hash: string;
  source_snapshot_hash: string;
  target_ref: {
    adapter: string;
    connection_id: string;
    resource: string;
    external_id: null;
  };
  before: Record<string, never>;
  after: Record<string, unknown> | null;
  required_approvals: number;
  decisions: { user_id: string; decision: string; decided_at?: string | null }[];
  invalidated_reason: string | null;
  expires_at: string | null;
}

export interface BusinessReceiptView {
  status: string;
  effect: string | null;
  external_id: string | null;
  approval_id?: string;
  proposal_content_hash?: string;
  target_resource?: string;
  before?: Record<string, never>;
  after?: Record<string, unknown> | null;
  readback?: Record<string, unknown> | null;
  cost?: {
    status?: string;
    amount_micros?: number | null;
    cost_scope?: string;
    live_provider_cost?: string;
    zia_cost?: string;
    currency?: string;
  } | null;
  human_next_step?: {
    title?: string;
    owner_role?: string;
    due_in_days?: number;
    instruction?: string;
    human_owned?: boolean;
  } | null;
  business_summary?: {
    accepted?: string[];
    not_accepted?: string[];
    purchase_intent_claimed?: boolean;
  } | null;
  receipt_hash?: string;
}

export interface BusinessRunView {
  schema: string;
  available: boolean;
  reason?: string;
  job_id: string;
  project_id?: string;
  workflow?: Record<string, unknown>;
  goal?: string;
  scenario_id?: string;
  context?: BusinessProjectContext;
  integration?: BusinessIntegrationState;
  steps?: BusinessStepView[];
  proposal?: BusinessProposalView | null;
  receipt?: BusinessReceiptView | null;
  next_human_task?: Record<string, unknown> | null;
  business_summary?: Record<string, unknown> | null;
  states?: Record<string, boolean>;
  synthetic_only?: boolean;
  live_connection_verified?: boolean;
}

export interface BusinessRunDetail {
  job: Job;
  business: BusinessRunView;
}

export interface ResearchSource {
  url: string;
  observed_at: string;
  claim: string;
}

export interface ResearchOrganization {
  name: string | null;
  type: string | null;
  country: string | null;
  city: string | null;
  website: string | null;
  language: string | null;
  member_estimate_band: string | null;
  facility_notes: string | null;
  field_provenance: Record<string, string>;
}

export interface ResearchEvaluation {
  organization_id: string;
  classification: "accept" | "needs_review" | "reject";
  reason_codes: string[];
  duplicate_of: string | null;
  fit_score: number;
  confidence: number;
  organization: ResearchOrganization;
  missing_fields: string[];
  sources: ResearchSource[];
}

export interface ResearchContractRejection {
  organization_id: string;
  code: string;
  detail: string;
}

export interface ResearchSummary {
  produced: number;
  accepted: number;
  needs_review: number;
  rejected: number;
}

export interface ResearchCost {
  known: boolean;
  actual_micros?: number;
  estimated_micros?: number;
  unavailable_count?: number;
  currency?: string;
}

export interface ResearchResultAvailable {
  schema_version: "r4.research_result.v1";
  state: "available";
  workflow: { key: string; version: number; content_hash: string };
  evaluated_on: string;
  snapshot_hash: string;
  source_hash: string;
  review_output_hash: string;
  fixture: { id: string; version: string; hash: string };
  summary: ResearchSummary;
  evaluations: ResearchEvaluation[];
  contract_rejections: ResearchContractRejection[];
  cost?: ResearchCost;
}

export interface ResearchResultUnavailable {
  schema_version: "r4.research_result.v1";
  state: "unavailable";
  reason: string;
}

export type ResearchResult = ResearchResultAvailable | ResearchResultUnavailable;


export type OpportunityOutcome =
  | "draft_candidate"
  | "needs_review"
  | "duplicate"
  | "rejected";

export interface OpportunitySourceClaim {
  field: string;
  value: string;
}

export interface OpportunitySource {
  source_id: string;
  url: string;
  observed_at: string;
  claim: string;
  claims: OpportunitySourceClaim[];
}

export interface OpportunityVerifiedFact {
  field: string;
  value: string;
  source_ids: string[];
}

export interface OpportunityInferredFact {
  field: string;
  value: string;
  status: string;
  basis: string;
}

export interface OpportunityContradiction {
  field: string;
  values: string[];
}

export interface OpportunityFitComponent {
  component: string;
  weight: number;
  evidence: string[];
}

export interface OpportunityRecord {
  organization_id: string;
  organization_name: string | null;
  organization_type: string | null;
  region: string | null;
  outcome: OpportunityOutcome;
  opportunity_type: string | null;
  fit_score: number | null;
  confidence: number | null;
  fit_components: OpportunityFitComponent[];
  reason_codes: string[];
  sources: OpportunitySource[];
  verified_facts: OpportunityVerifiedFact[];
  inferred_facts: OpportunityInferredFact[];
  missing_fields: string[];
  contradictions: OpportunityContradiction[];
  uncited_fields: string[];
  dedupe_key: string | null;
  duplicate_of: string | null;
  suppression: { suppressed: boolean; reasons: string[] };
  recommended_next_action: string;
  recommendation_draft_only: true;
  machine_classification_only: true;
  human_review_status: "pending";
  human_review_required: true;
  human_review_minutes: null;
  contract_error_code: string | null;
}

export interface OpportunitySummary {
  produced: number;
  draft_candidate: number;
  needs_review: number;
  duplicate: number;
  rejected: number;
  human_review_pending: number;
  contract_rejected: number;
}

export interface OpportunityResultAvailable {
  schema_version: "r5.opportunity_result.v1";
  state: "available";
  workflow: { key: string; version: number; content_hash: string };
  project_id: string;
  evaluated_on: string;
  fixture: { id: string; version: string; hash: string };
  snapshot_hash: string;
  analysis_output_hash: string;
  review_output_hash: string;
  decisions_hash: string;
  summary: OpportunitySummary;
  records: OpportunityRecord[];
  cost?: ResearchCost;
}

export interface OpportunityResultUnavailable {
  schema_version: "r5.opportunity_result.v1";
  state: "unavailable";
  reason: string;
}

export type OpportunityResult =
  | OpportunityResultAvailable
  | OpportunityResultUnavailable;

/** Project-persistent, team-shared opportunity group (R5-B extension). */
export interface OpportunityGroupSource {
  job_id: string;
  decisions_hash: string;
  analysis_output_hash: string;
  review_output_hash: string;
  snapshot_hash: string;
  workflow: { key: "megabooker.opportunity_analysis"; version: 1; content_hash: string };
  evaluated_on: string;
}

export interface OpportunityGroup {
  schema_version: "r5.opportunity_group.v1";
  id: string;
  project_id: string;
  name: string;
  note: string;
  organization_ids: string[];
  source: OpportunityGroupSource;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  version: number;
}

export interface OpportunityGroupPage {
  items: OpportunityGroup[];
  next_cursor: string | null;
}

export interface OpportunityGroupCreateInput {
  name: string;
  note: string;
  organization_ids: string[];
  source_job_id: string;
  source_decisions_hash: string;
}

export interface OpportunityGroupUpdateInput {
  version: number;
  name?: string;
  note?: string;
  organization_ids?: string[];
}

/** Draft PR23: saved-group additional analysis (deterministic + official sites). */
export interface GroupAnalysisOfficialHostEvidence {
  source_id: string;
  source_url: string;
  observed_at: string;
  website_value: string;
  claim: string;
  evidence_hash: string;
}

export interface GroupAnalysisOfficialHost {
  organization_id: string;
  status: "eligible" | "needs_verification";
  host?: string;
  url?: string;
  reason?: string;
  evidence?: GroupAnalysisOfficialHostEvidence;
}

export interface GroupAnalysisPreflightOrganization {
  organization_id: string;
  organization_name: string | null;
  official_host: GroupAnalysisOfficialHost;
}

export interface GroupAnalysisLimits {
  version: "group_analysis.policy.v1";
  max_organizations: 20;
  max_pages_per_organization: 5;
  max_depth: 1;
  max_page_bytes: 1048576;
  request_timeout_seconds: 10;
  max_run_seconds: 300;
  concurrency: 2;
  max_redirects: 3;
  robots: "required";
}

export interface GroupAnalysisCapabilities {
  existing_data_elaboration: "available" | "completed";
  official_site_research: "available" | "completed" | "unavailable";
  semantic_analysis: "not_evaluated";
  semantic_provider: "not_configured";
  semantic_reason: "semantic_provider_not_activated_for_product";
}

export interface GroupAnalysisPreflight {
  schema_version: "group_analysis.preflight.v1";
  state: "ready";
  project_id: string;
  reference_date: string;
  group: {
    id: string;
    name: string;
    note: string;
    version: number;
    organization_count: number;
    organization_ids: string[];
  };
  source: OpportunityGroupSource;
  question: { max_length: 2000; suggested: string };
  organizations: GroupAnalysisPreflightOrganization[];
  official_hosts: { eligible: number; needs_verification: number; max: 20 };
  capabilities: GroupAnalysisCapabilities;
  limits: GroupAnalysisLimits;
  model_activation: { status: "not_requested"; note: string };
}

export interface GroupAnalysisObservation {
  kind: string;
  text: string;
  page_url: string;
  observed_at: string;
  content_hash: string;
}

export interface GroupAnalysisPage {
  url: string;
  fetched_at: string;
  content_hash: string;
  status_code: number;
  title: string | null;
  description: string | null;
  public_links: string[];
  observations: GroupAnalysisObservation[];
}

export interface GroupAnalysisOrganizationResult {
  organization_id: string;
  organization_name: string | null;
  existing_evidence: {
    state: "available";
    supported_facts: { field: string; value: string; source_ids: string[] }[];
    missing_fields: string[];
    uncited_fields: string[];
    contradictions: { field: string; values: string[] }[];
    sources: { source_id: string; url: string; observed_at: string }[];
  };
  official_site: {
    state: "observed" | "partial" | "unavailable" | "not_attempted";
    host: string | null;
    pages: GroupAnalysisPage[];
    limitations: string[];
  };
  comparison: {
    state: "supported" | "differs" | "new_observation" | "not_comparable" | "unavailable";
    items: {
      field: string;
      prior_value: string;
      observed_value: string;
      relation: "agrees" | "differs";
      page_url: string;
    }[];
  };
  inference: {
    kind: string;
    text: string;
    basis: string;
    page_url: string;
  }[];
  unknowns: { kind: string; reason: string }[];
  conflicts: { field: string; values: string[]; basis?: string }[];
  semantic_analysis: "not_evaluated";
  human_review_status: "pending";
}

export interface GroupAnalysisResultAvailable {
  schema_version: "group_analysis.result.v1";
  state: "available";
  reason: null;
  workflow: {
    key: "megabooker.group_additional_analysis";
    version: 1;
    content_hash: string;
  };
  project_id: string;
  group: { id: string; version: number; name: string; organization_count: number };
  source: OpportunityGroupSource;
  question: { text: string; hash: string };
  snapshot_hash: string;
  job_status: string;
  capabilities: GroupAnalysisCapabilities;
  limits: GroupAnalysisLimits;
  cost?: {
    known: boolean;
    actual_micros: number;
    estimated_micros: number;
    unavailable_count: number;
    currency: "USD";
    basis: "measured_zero_no_paid_service";
    semantic_cost: { status: "unknown"; reason: "provider_not_configured" };
  };
  summary: {
    organizations: number;
    eligible_hosts: number;
    pages_fetched: number;
    pages_unavailable: number;
    new_observations: number;
    conflicts: number;
    unknowns: number;
    semantic_not_evaluated: number;
  };
  organizations: GroupAnalysisOrganizationResult[];
}

export interface GroupAnalysisResultUnavailable {
  schema_version: "group_analysis.result.v1";
  state: "unavailable";
  reason: string;
  job_status: string;
  progress?: { completed: number; unknown: number; failed: number; intent: number };
}

export type GroupAnalysisResult =
  | GroupAnalysisResultAvailable
  | GroupAnalysisResultUnavailable;

export interface GroupAnalysisRun {
  id: string;
  status: JobStatus;
  group_analysis: {
    group_id: string;
    group_version: number;
    snapshot_hash: string;
    request_fingerprint: string;
    source: OpportunityGroupSource;
  };
}

export interface GroupAnalysisRunPage {
  items: GroupAnalysisRun[];
  next_cursor?: string | null;
}

/** Owner-authorized draft PR22 team fields, manual values and personal layouts. */
export type OpportunityFieldType = "text" | "number" | "date" | "select";

export interface OpportunityFieldOption {
  id: string;
  label: string;
  archived: boolean;
}

export interface OpportunityFieldDefinition {
  id: string;
  label: string;
  type: OpportunityFieldType;
  options: OpportunityFieldOption[];
  archived: boolean;
}

export interface OpportunityLayout {
  order: string[];
  hidden: string[];
}

export interface OpportunityFieldSettings {
  schema_version: "r5.opportunity_field_settings.v1";
  id: string | null;
  project_id: string;
  definitions: OpportunityFieldDefinition[];
  shared_layout: OpportunityLayout;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
  version: number;
}

export interface OpportunityFieldCreateInput {
  version: number;
  label: string;
  type: OpportunityFieldType;
  options?: { label: string }[];
}

export interface OpportunityFieldUpdateInput {
  version: number;
  label?: string;
  add_options?: { label: string }[];
  archive_option_ids?: string[];
  archive?: boolean;
}

export interface OpportunitySharedLayoutInput {
  version: number;
  order: string[];
  hidden: string[];
}

export type OpportunityManualValue = string | number;

export interface OpportunityFieldValue {
  field_id: string;
  value: OpportunityManualValue;
}

export interface OpportunityFieldValueDocument {
  schema_version: "r5.opportunity_field_value.v1";
  id: string;
  project_id: string;
  source: { job_id: string; decisions_hash: string };
  organization_id: string;
  values: OpportunityFieldValue[];
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  version: number;
}

export interface OpportunityManualValueWriteCapability {
  supported: boolean;
  reason: "feature_requires_replica_set" | null;
}

export interface OpportunityFieldValuePage {
  items: OpportunityFieldValueDocument[];
  manual_value_write: OpportunityManualValueWriteCapability;
}

export interface OpportunityFieldValueChange {
  field_id: string;
  value: OpportunityManualValue | null;
}

export interface OpportunityFieldValuePatchInput {
  version: number;
  source_job_id: string;
  source_decisions_hash: string;
  values: OpportunityFieldValueChange[];
}

export interface OpportunityPersonalView {
  schema_version: "r5.opportunity_personal_view.v1";
  id: string;
  project_id: string;
  user_id: string;
  layout: OpportunityLayout | null;
  use_project_default: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface OpportunityPersonalViewEnvelope {
  item: OpportunityPersonalView | null;
}

export interface OpportunityPersonalViewInput {
  version: number;
  layout: OpportunityLayout | null;
}

export interface OpportunityFieldsLoader {
  getOpportunityFieldSettings(projectId: string): Promise<OpportunityFieldSettings>;
  createOpportunityField(
    projectId: string,
    input: OpportunityFieldCreateInput,
  ): Promise<OpportunityFieldSettings>;
  updateOpportunityField(
    projectId: string,
    fieldId: string,
    input: OpportunityFieldUpdateInput,
  ): Promise<OpportunityFieldSettings>;
  putOpportunitySharedLayout(
    projectId: string,
    input: OpportunitySharedLayoutInput,
  ): Promise<OpportunityFieldSettings>;
  listOpportunityFieldValues(
    projectId: string,
    options: { source_job_id: string; source_decisions_hash: string },
  ): Promise<OpportunityFieldValuePage>;
  patchOpportunityFieldValues(
    projectId: string,
    organizationId: string,
    input: OpportunityFieldValuePatchInput,
  ): Promise<OpportunityFieldValueDocument>;
  getOpportunityPersonalView(projectId: string): Promise<OpportunityPersonalViewEnvelope>;
  putOpportunityPersonalView(
    projectId: string,
    input: OpportunityPersonalViewInput,
  ): Promise<OpportunityPersonalView>;
}

export interface JobDetail extends Job {
  steps: JobStep[];
  group_analysis?: GroupAnalysisRun["group_analysis"];
  /**
   * R4-C read-only projection. Present only for `g4.lead_research` jobs; legacy
   * mock jobs deliberately keep their previous detail shape.
   */
  result?: ResearchResult | OpportunityResult | GroupAnalysisResult | null;
}

export type Risk = "medium" | "high" | "critical";

export interface ApprovalDecision {
  user_id: string;
  decision: "approve" | "reject";
  self_approval: boolean;
  decided_at: string;
  justification: string | null;
}

export interface Approval {
  id: string;
  project_id: string;
  job_id: string;
  step_id: string;
  action_type: string;
  risk: Risk;
  status: string;
  required_approvals: number;
  approvals_recorded: number;
  content_hash: string;
  requested_by: string | null;
  proposed_by: { type: string; id: string } | null;
  expires_at: string | null;
  created_at: string | null;
  invalidated_reason: string | null;
  needs_review_reason?: string | null;
  review_quarantine_until?: string | null;
  hard_limit_guaranteed?: boolean | null;
  budget_context?: {
    cost_bound: string;
    cap_micros: number | null;
    assumed_max_micros: number | null;
    hard_limit_guaranteed: boolean;
  } | null;
  receipt: {
    external_id: string | null;
    effect: "applied" | "none" | "unknown";
    executed_at: string;
    source?: string;
  } | null;
  decisions: ApprovalDecision[];
}

export interface ApprovalDetail extends Approval {
  payload: Record<string, unknown> | null;
  diff: { op: string; path: string; value?: unknown }[];
  target_ref: Record<string, unknown> | null;
}

export interface BudgetSummary {
  budget_id: string;
  period: string;
  currency: string;
  soft_limit_micros: number;
  hard_limit_micros: number | null;
  limits_version?: number;
  reserved_micros: number;
  spent_micros: number;
  available_micros: number | null;
  frozen_at: string | null;
  frozen_reason: string | null;
  guarantee_broken_at: string | null;
  hard_limit_guaranteed: boolean;
  unavailable_count: number;
  unbounded_open_count: number;
  open_reservations: number;
}

export interface CostEntry {
  id: string;
  entry_type: "reservation" | "charge" | "release" | "adjustment";
  cost_status: "actual" | "estimated" | "unavailable" | null;
  amount_micros: number | null;
  overrun_micros: number | null;
  reservation_id: string | null;
  job_id: string | null;
  created_at: string | null;
}

export interface CostSummary {
  period: string;
  actual_micros: number;
  estimated_micros: number;
  unavailable_count: number;
  reserved_micros: number;
}

export interface AuditVerifyResult {
  chain_id: string;
  ok: boolean;
  checked_entries: number;
  head_seq: number;
  head_hash: string;
  first_bad_seq: number | null;
  reason: string | null;
  last_anchor: { seq: number; hash: string; anchored_at?: string } | null;
}

export interface Digest {
  project_id: string;
  date: string;
  jobs: {
    created: number;
    succeeded: number;
    failed: number;
    needs_review: number;
    killed: number;
  };
  approvals: {
    proposed: number;
    approved: number;
    rejected: number;
    expired: number;
    invalidated: number;
    owner_overrides: number;
    self_approvals?: number;
  };
  cost: CostSummary;
  budget_alerts: string[];
  audit_anchor: { seq: number; hash: string } | null;
}

export interface OrgUser {
  id: string;
  email: string;
  display_name: string;
  status: string;
  is_org_owner: boolean;
  locale: string;
  last_login_at: string | null;
  totp_enabled?: boolean;
}

export interface Membership {
  id: string;
  user_id: string;
  project_id: string;
  role: string;
  granted_by: string | null;
  granted_at: string | null;
}

export interface ApprovalPolicy {
  version: number;
  rules: {
    expiry_hours: number;
    medium: { approver_roles: string[]; self_approval: boolean };
    high: { approver_roles: string[]; second_approver: boolean };
    critical: {
      two_person: boolean;
      approver_user_ids: string[];
      owner_override_min_justification: number;
    };
  };
  created_by: string | null;
  created_at: string | null;
}

export interface AuditEntry {
  id: string;
  seq: number;
  action: string;
  actor: { type: string; id: string; session_id?: string };
  target: { type: string; id: string };
  ts: string;
  data: Record<string, unknown>;
  hash: string;
  prev_hash: string;
}

export interface KillSwitchState {
  project_engaged: boolean;
  project_reason: string | null;
  project_changed_at: string | null;
  global_engaged: boolean;
}

export interface Overview {
  jobs: Record<JobStatus, number> & Record<string, number>;
  pending_approvals: number;
  budget: BudgetSummary | null;
  kill_switch: KillSwitchState;
  audit_records: number;
}

/** A structured API failure so the UI can show a precise, actionable message. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }

  /** Human-readable Croatian message for the cases the UI can act on. */
  get messageHr(): string {
    switch (this.detail) {
      case "content_hash_mismatch":
        return "Odobrenje se promijenilo otkad ste ga vidjeli. Ponovno učitajte i pregledajte.";
      case "not_found":
        return "Nema pristupa ili zapis ne postoji.";
      case "forbidden":
        return "Nemate ovlast za ovu radnju.";
      case "job_already_finished":
        return "Posao je već završen.";
      case "at_least_one_synthetic_lead_required":
        return "Potreban je barem jedan sintetički lead.";
      case "group_name_exists":
        return "Grupa s tim nazivom već postoji na projektu.";
      case "invalid_name":
        return "Naziv grupe mora sadržavati barem jedan znak i imati najviše 80 znakova.";
      case "invalid_note":
        return "Bilješka grupe može imati najviše 2000 znakova.";
      case "invalid_organization_id":
        return "Odabrani zapis nema ispravan identifikator organizacije.";
      case "duplicate_organization_id":
        return "Isti zapis je odabran više puta; odabir mora biti jedinstven.";
      case "invalid_organization_ids":
        return "Grupa mora sadržavati između 1 i 20 jedinstvenih zapisa.";
      case "group_limit_reached":
        return "Na projektu je dosegnuta granica od 100 grupa.";
      case "group_conflict":
        return "Grupa se promijenila u međuvremenu. Osvježite popis grupa.";
      case "group_write_unavailable":
        return "Grupa nije potvrđeno spremljena; osvježite popis prije ponovnog pokušaja.";
      case "version_conflict":
        return "Netko je promijenio grupu prije vas. Osvježite popis i pokušajte ponovno.";
      case "source_not_succeeded":
        return "Izvorna analiza još nije uspješno završena; grupa se ne može spremiti.";
      case "invalid_source":
        return "Izvorna analiza nije provjerena kroz cijeli lanac dokaza; grupa se ne može spremiti.";
      case "source_decisions_mismatch":
        return "Izvorna analiza se promijenila u odnosu na prikazane odluke. Osvježite analizu.";
      case "unknown_organization":
        return "Odabrani zapis ne postoji u izvornoj analizi. Osvježite analizu.";
      case "no_changes":
        return "Nijedno polje grupe nije promijenjeno.";
      case "invalid_limit":
        return "Neispravan broj grupa po stranici.";
      case "invalid_cursor":
        return "Neispravan pokazivač stranice grupa; osvježite popis.";
      case "outbox_backpressure":
        return "Audit rep je pun ili blokiran; promjena nije spremljena. Pokušajte kasnije.";
      case "unsupported_workflow":
        return "Taj workflow nije podržan u G1a.";
      case "auth_signing_key_unavailable":
        return "Poslužitelj nije konfiguriran za prijavu (nedostaje ključ).";
      default:
        return this.detail || `Greška (HTTP ${this.status}).`;
    }
  }
}

let accessToken: string | null = null;
// Bumped on every sign-in, sign-out and foreign-account drop (every `setAccessToken` call). A restore that
// was in flight or queued while that happened is stale and must not install anything (UI-SES-19/22). A
// restore itself only refreshes the token of the SAME session and does not bump it.
let authEpoch = 0;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  authEpoch += 1;
}

/** A session restore finished after the token it was started for had been dropped or replaced. */
export class StaleRestoreError extends Error {
  constructor() {
    super("stale session restore");
    this.name = "StaleRestoreError";
  }
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/** Response of `POST /api/auth/restore`. */
export interface RestoreResult {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  idle_minutes: number;
}

/**
 * The single in-flight session restore, if any. `key` distinguishes "no argument" from
 * a concrete idle value so equal calls share one request and different ones queue.
 */
interface QueuedRestore {
  key: string;
  promise: Promise<RestoreResult>;
}

/** The LAST restore asked for (running or queued). New calls either join it (same window) or run after it. */
let tailRestore: QueuedRestore | null = null;

function idleKey(idleMinutes: number | undefined): string {
  return idleMinutes === undefined ? "default" : `idle:${idleMinutes}`;
}

async function sendRestore(idleMinutes: number | undefined, epoch: number): Promise<RestoreResult> {
  let body: string | undefined;
  if (idleMinutes !== undefined) body = JSON.stringify({ idle_minutes: idleMinutes });
  const result = await request<RestoreResult>("/api/auth/restore", {
    method: "POST",
    auth: false,
    body,
    headers: { "X-Requested-With": "megaai" },
  });
  if (epoch !== authEpoch) throw new StaleRestoreError();
  accessToken = result.access_token;
  return result;
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const { auth = true, headers, ...rest } = init;
  const finalHeaders: Record<string, string> = {
    // A body-less POST (e.g. session restore without `idle_minutes`) must not claim a
    // JSON body: no `Content-Type` unless the caller asked for one.
    ...(rest.body === undefined ? {} : { "Content-Type": "application/json" }),
    ...(headers as Record<string, string> | undefined),
  };
  if (auth) {
    if (!accessToken) throw new ApiError(401, "unauthorized");
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  // `credentials` is included for `/api/auth/*` so the refresh cookie is sent; the cookie
  // itself is scoped to those routes by the API.
  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: finalHeaders,
    credentials: "include",
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const detail =
      body && typeof body === "object" && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, detail);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** `REACT_APP_BACKEND_URL` must be configured; otherwise every call would 404. */
export function backendConfigured(): boolean {
  return BASE_URL.length > 0;
}

export function backendBaseUrl(): string {
  return BASE_URL;
}

export const api = {
  integrations: (projectId: string, signal?: AbortSignal) => request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/integrations`, {signal}),
  integrationMutation: (projectId: string, path: string, payload: unknown, method: string, signal?: AbortSignal) => request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/integrations${path}`, {method, body: JSON.stringify(payload), signal}),
  capabilities: (projectId: string, signal?: AbortSignal) => request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/capabilities`, {signal}),
  coordinator: (projectId: string, signal?: AbortSignal) => request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/coordinator`, {signal}),
  enableCoordinator: (projectId: string, payload: unknown, signal?: AbortSignal) => request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/coordinator/enable`, {method: "POST", body: JSON.stringify(payload), signal}),
  sendCoordinatorMessage: (projectId: string, payload: unknown, signal?: AbortSignal) => request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/coordinator/messages`, {method: "POST", body: JSON.stringify(payload), signal}),
  saveWorkPlan: (projectId: string, payload: unknown) =>
    request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/work-register/plan`,
      {method: "PUT", body: JSON.stringify(payload)}),
  workRegister: (projectId: string, signal?: AbortSignal) => request<unknown>(`/api/projects/${encodeURIComponent(projectId)}/work-register`, { signal }),
  async login(
    email: string,
    password: string,
    idleMinutes?: number,
  ): Promise<{ access_token: string }> {
    // `idle_minutes` is omitted entirely unless the caller set it, so the API keeps its
    // own default instead of receiving `undefined` / `null`.
    const body: { email: string; password: string; idle_minutes?: number } = {
      email,
      password,
    };
    if (idleMinutes !== undefined) body.idle_minutes = idleMinutes;
    const result = await request<{ access_token: string }>("/api/auth/login", {
      method: "POST",
      auth: false,
      body: JSON.stringify(body),
      headers: { "X-Requested-With": "megaai" },
    });
    setAccessToken(result.access_token);
    return result;
  },

  /**
   * Restore a session from the `HttpOnly` refresh cookie after a page reload.
   *
   * `idle_minutes` is only sent when provided; without it the API keeps the idle value
   * stored with the session. Single-flight rules:
   *  - concurrent calls with the same argument share one request;
   *  - a call with a different argument waits for the in-flight request (ignoring its
   *    outcome) and then sends its own.
   *
   * The current token is replaced only on success; 401/422/network failures leave it
   * untouched.
   */
  restore(idleMinutes?: number): Promise<RestoreResult> {
    const key = idleKey(idleMinutes);
    const epoch = authEpoch; // the epoch of THIS invocation: a queued call must not outlive a sign-out/sign-in
    const tail = tailRestore;
    // Same window as the last one asked for (running or still queued): share it. A different window
    // never merges backwards - the last asked value must be the last one the server sees.
    if (tail !== null && tail.key === key) return tail.promise;
    const run: Promise<RestoreResult> =
      tail === null
        ? sendRestore(idleMinutes, epoch)
        : tail.promise
            .catch(() => undefined)
            .then(() => {
              if (epoch !== authEpoch) throw new StaleRestoreError();
              return sendRestore(idleMinutes, epoch);
            });
    const entry: QueuedRestore = { key, promise: run };
    tailRestore = entry;
    const settle = (): void => {
      if (tailRestore === entry) tailRestore = null;
    };
    run.then(settle, settle);
    return run;
  },

  async logout(): Promise<void> {
    // `request` reads the token synchronously when called, so the in-flight logout keeps its
    // credential. The token is dropped locally at once; it must NOT be cleared again after the
    // call settles (a late completion would wipe the token of a session started meanwhile).
    const pending = request<void>("/api/auth/logout", { method: "POST" });
    setAccessToken(null);
    await pending;
  },

  me(): Promise<CurrentUser> {
    return request<CurrentUser>("/api/auth/me");
  },

  listProjects(): Promise<Project[]> {
    return request<Project[]>("/api/projects");
  },

  projectOverview(projectId: string): Promise<Overview> {
    return request<Overview>(`/api/projects/${projectId}/overview`);
  },

  getMinbarPilotResult(
    projectId: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<unknown> {
    return request<unknown>(
      `/api/projects/${encodeURIComponent(projectId)}/pilot-result`,
      { signal: options.signal },
    );
  },

  listJobs(
    projectId: string,
    options: { status?: JobStatus; cursor?: string | null; archived?: boolean } = {},
  ): Promise<{ items: Job[]; next_cursor?: string | null }> {
    // The API orders jobs by random UUID `_id`, not by `created_at`. Callers that need
    // the newest job must follow `next_cursor` through every page instead of sorting a
    // single 50-row page.
    const params = new URLSearchParams();
    if (options.status) params.set("status", options.status);
    if (options.archived) params.set("archived", "true");
    if (options.cursor) params.set("cursor", options.cursor);
    const query = params.toString() ? `?${params.toString()}` : "";
    return request<{ items: Job[]; next_cursor?: string | null }>(
      `/api/projects/${projectId}/jobs${query}`,
    );
  },

  getJob(projectId: string, jobId: string): Promise<JobDetail> {
    return request<JobDetail>(`/api/projects/${projectId}/jobs/${jobId}`);
  },

  startJob(
    projectId: string,
    input: { seed: number; leads: unknown[]; idempotency_key: string },
  ): Promise<Job> {
    return request<Job>(`/api/projects/${projectId}/jobs`, {
      method: "POST",
      body: JSON.stringify({ workflow_key: "mock_lead_pipeline", ...input }),
    });
  },

  startOpportunityJob(
    projectId: string,
    input: { idempotency_key: string },
  ): Promise<Job> {
    // The existing job creation endpoint; only the server-owned workflow pair differs.
    return request<Job>(`/api/projects/${projectId}/jobs`, {
      method: "POST",
      body: JSON.stringify({
        workflow_key: "megabooker_opportunity_analysis",
        workflow_version: 1,
        input: {},
        idempotency_key: input.idempotency_key,
      }),
    });
  },

  getBusinessCatalogue(projectId: string): Promise<BusinessCatalogueResponse> {
    return request<BusinessCatalogueResponse>(
      `/api/projects/${projectId}/business-orchestration/catalogue`,
    );
  },

  startBusinessRun(
    projectId: string,
    input: { goal: string; scenario_id: "current_evidence" | "research_gap"; idempotency_key: string },
  ): Promise<Job> {
    return request<Job>(
      `/api/projects/${projectId}/business-orchestration/runs`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  listBusinessRuns(projectId: string): Promise<{ items: Job[] }> {
    return request<{ items: Job[] }>(
      `/api/projects/${projectId}/business-orchestration/runs`,
    );
  },

  getBusinessRun(projectId: string, jobId: string): Promise<BusinessRunDetail> {
    return request<BusinessRunDetail>(
      `/api/projects/${projectId}/business-orchestration/runs/${encodeURIComponent(jobId)}`,
    );
  },

  listOpportunityGroups(
    projectId: string,
    options: { cursor?: string | null; limit?: number } = {},
  ): Promise<OpportunityGroupPage> {
    const params = new URLSearchParams();
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    const query = params.toString() ? `?${params.toString()}` : "";
    return request<OpportunityGroupPage>(
      `/api/projects/${projectId}/opportunity-groups${query}`,
    );
  },

  createOpportunityGroup(
    projectId: string,
    input: OpportunityGroupCreateInput,
  ): Promise<OpportunityGroup> {
    return request<OpportunityGroup>(
      `/api/projects/${projectId}/opportunity-groups`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  updateOpportunityGroup(
    projectId: string,
    groupId: string,
    input: OpportunityGroupUpdateInput,
  ): Promise<OpportunityGroup> {
    return request<OpportunityGroup>(
      `/api/projects/${projectId}/opportunity-groups/${encodeURIComponent(groupId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  getGroupAnalysisPreflight(
    projectId: string,
    groupId: string,
  ): Promise<GroupAnalysisPreflight> {
    return request<GroupAnalysisPreflight>(
      `/api/projects/${projectId}/opportunity-groups/${encodeURIComponent(groupId)}/additional-analysis`,
    );
  },

  startGroupAnalysis(
    projectId: string,
    groupId: string,
    input: { group_version: number; question: string; idempotency_key: string },
  ): Promise<Job & { group_analysis?: GroupAnalysisRun["group_analysis"] }> {
    return request<Job & { group_analysis?: GroupAnalysisRun["group_analysis"] }>(
      `/api/projects/${projectId}/opportunity-groups/${encodeURIComponent(groupId)}/additional-analysis`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  listGroupAnalysisRuns(
    projectId: string,
    groupId: string,
    options: { cursor?: string | null; limit?: number } = {},
  ): Promise<GroupAnalysisRunPage> {
    const params = new URLSearchParams();
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    const query = params.toString() ? `?${params.toString()}` : "";
    return request<GroupAnalysisRunPage>(
      `/api/projects/${projectId}/opportunity-groups/${encodeURIComponent(groupId)}/additional-analysis/runs${query}`,
    );
  },

  getOpportunityFieldSettings(projectId: string): Promise<OpportunityFieldSettings> {
    return request<OpportunityFieldSettings>(
      `/api/projects/${projectId}/opportunity-field-settings`,
    );
  },

  createOpportunityField(
    projectId: string,
    input: OpportunityFieldCreateInput,
  ): Promise<OpportunityFieldSettings> {
    return request<OpportunityFieldSettings>(
      `/api/projects/${projectId}/opportunity-field-settings/fields`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  updateOpportunityField(
    projectId: string,
    fieldId: string,
    input: OpportunityFieldUpdateInput,
  ): Promise<OpportunityFieldSettings> {
    return request<OpportunityFieldSettings>(
      `/api/projects/${projectId}/opportunity-field-settings/fields/${encodeURIComponent(fieldId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  putOpportunitySharedLayout(
    projectId: string,
    input: OpportunitySharedLayoutInput,
  ): Promise<OpportunityFieldSettings> {
    return request<OpportunityFieldSettings>(
      `/api/projects/${projectId}/opportunity-field-settings/shared-layout`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },

  listOpportunityFieldValues(
    projectId: string,
    options: { source_job_id: string; source_decisions_hash: string },
  ): Promise<OpportunityFieldValuePage> {
    const params = new URLSearchParams({
      source_job_id: options.source_job_id,
      source_decisions_hash: options.source_decisions_hash,
    });
    return request<OpportunityFieldValuePage>(
      `/api/projects/${projectId}/opportunity-field-values?${params.toString()}`,
    );
  },

  patchOpportunityFieldValues(
    projectId: string,
    organizationId: string,
    input: OpportunityFieldValuePatchInput,
  ): Promise<OpportunityFieldValueDocument> {
    return request<OpportunityFieldValueDocument>(
      `/api/projects/${projectId}/opportunity-field-values/${encodeURIComponent(organizationId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },

  getOpportunityPersonalView(
    projectId: string,
  ): Promise<OpportunityPersonalViewEnvelope> {
    return request<OpportunityPersonalViewEnvelope>(
      `/api/projects/${projectId}/opportunity-personal-view`,
    );
  },

  putOpportunityPersonalView(
    projectId: string,
    input: OpportunityPersonalViewInput,
  ): Promise<OpportunityPersonalView> {
    return request<OpportunityPersonalView>(
      `/api/projects/${projectId}/opportunity-personal-view`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },

  archiveJob(projectId: string, jobId: string, archived: boolean): Promise<unknown> {
    return request(`/api/projects/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}/archive`,
      { method: "PUT", body: JSON.stringify({ archived }) });
  },

  cancelJob(projectId: string, jobId: string): Promise<{ status: string }> {
    return request<{ status: string }>(
      `/api/projects/${projectId}/jobs/${jobId}/cancel`,
      { method: "POST" },
    );
  },

  listApprovals(projectId: string, status?: string): Promise<{ items: Approval[] }> {
    const query = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<{ items: Approval[] }>(`/api/projects/${projectId}/approvals${query}`);
  },

  getApproval(projectId: string, approvalId: string): Promise<ApprovalDetail> {
    return request<ApprovalDetail>(`/api/projects/${projectId}/approvals/${approvalId}`);
  },

  decide(
    projectId: string,
    approvalId: string,
    input: {
      decision: "approve" | "reject";
      content_hash: string;
      justification?: string;
      self_approval_acknowledged?: boolean;
      owner_override?: boolean;
      assumed_max_micros?: number;
    },
  ): Promise<{ status: string; approvals_recorded: number }> {
    return request<{ status: string; approvals_recorded: number }>(
      `/api/projects/${projectId}/approvals/${approvalId}/decisions`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  resolveJobReview(
    projectId: string,
    jobId: string,
    input: {
      resolution:
        | "reconcile"
        | "confirm_executed"
        | "confirm_not_executed"
        | "mark_failed"
        | "requeue_internal_step";
      justification: string;
      reservation_outcome?: "charge_estimated" | "charge_actual" | "release";
      actual_cost_micros?: number;
      evidence?: {
        external_id?: string;
        note?: string;
        finality_evidence_digest?: string;
      };
    },
  ): Promise<JobDetail> {
    return request<JobDetail>(
      `/api/projects/${projectId}/jobs/${jobId}/resolve-review`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },

  budget(projectId: string, period?: string): Promise<BudgetSummary> {
    const query = period ? `?period=${encodeURIComponent(period)}` : "";
    return request<BudgetSummary>(`/api/projects/${projectId}/budgets${query}`);
  },

  costSummary(projectId: string, period?: string): Promise<CostSummary> {
    const query = period ? `?period=${encodeURIComponent(period)}` : "";
    return request<CostSummary>(
      `/api/projects/${projectId}/costs/summary${query}`,
    );
  },

  unfreezeBudget(
    projectId: string,
    period: string,
    justification: string,
  ): Promise<BudgetSummary> {
    return request<BudgetSummary>(
      `/api/projects/${projectId}/budgets/${period}/unfreeze`,
      { method: "POST", body: JSON.stringify({ justification }) },
    );
  },

  initializeMonthlyBudget(projectId: string): Promise<BudgetSummary> {
    return request<BudgetSummary>(`/api/projects/${projectId}/budgets/initialize`, { method: "POST" });
  },

  resumeWaitingRuns(projectId: string): Promise<{ resumed_runs: number }> {
    return request<{ resumed_runs: number }>(`/api/projects/${projectId}/budgets/resume-waiting`, { method: "POST" });
  },

  listCosts(projectId: string): Promise<{ items: CostEntry[] }> {
    return request<{ items: CostEntry[] }>(`/api/projects/${projectId}/costs`);
  },

  listAudit(projectId: string): Promise<{ items: AuditEntry[] }> {
    return request<{ items: AuditEntry[] }>(`/api/projects/${projectId}/audit`);
  },

  verifyAudit(projectId: string): Promise<AuditVerifyResult> {
    return request<AuditVerifyResult>(
      `/api/projects/${projectId}/audit/verify`,
    );
  },

  projectDigest(projectId: string, date?: string): Promise<Digest> {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    return request<Digest>(`/api/projects/${projectId}/digest${query}`);
  },

  listMembers(projectId: string): Promise<Membership[]> {
    return request<Membership[]>(`/api/projects/${projectId}/members`);
  },

  listOrgUsers(): Promise<OrgUser[]> {
    return request<OrgUser[]>("/api/org/users");
  },

  getApprovalPolicy(): Promise<ApprovalPolicy> {
    return request<ApprovalPolicy>("/api/org/policies/approval");
  },

  getGlobalKillSwitch(): Promise<{
    scope: string;
    engaged: boolean;
    reason: string | null;
  }> {
    return request("/api/org/kill-switch");
  },

  setGlobalKillSwitch(
    engaged: boolean,
    reason: string,
  ): Promise<{ engaged: boolean; approvals_invalidated: number }> {
    return request("/api/org/kill-switch", {
      method: "PUT",
      body: JSON.stringify({ engaged, reason }),
    });
  },

  killSwitch(projectId: string): Promise<KillSwitchState> {
    return request<KillSwitchState>(`/api/projects/${projectId}/kill-switch`);
  },

  setKillSwitch(
    projectId: string,
    engaged: boolean,
    reason?: string,
  ): Promise<KillSwitchState & { approvals_invalidated: number }> {
    const query = new URLSearchParams({ engaged: String(engaged) });
    if (reason) query.set("reason", reason);
    return request<KillSwitchState & { approvals_invalidated: number }>(
      `/api/projects/${projectId}/kill-switch?${query.toString()}`,
      { method: "PUT" },
    );
  },
};

/** Micro-USD as a readable USD string. Integer math only; never a float amount. */
export function formatMicros(micros: number | null | undefined): string {
  if (micros === null || micros === undefined) return "—";
  const negative = micros < 0;
  const abs = Math.abs(micros);
  const whole = Math.floor(abs / 1_000_000);
  const cents = Math.floor((abs % 1_000_000) / 10_000);
  return `${negative ? "-" : ""}$${whole.toLocaleString("en-US")}.${String(cents).padStart(2, "0")}`;
}
